<script setup lang="ts">
import { computed, ref } from "vue"
import { Cropper, CircleStencil } from "vue-advanced-cropper"
import "vue-advanced-cropper/dist/style.css"
import { RotateCw, ZoomIn, ZoomOut } from "lucide-vue-next"
import { toast } from "vue-sonner"
import * as Sentry from "@sentry/nuxt"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { usePlatform } from "~/composables/usePlatform"
import { useImageCropper } from "~/composables/useImageCropper"

// The crop step every avatar upload goes through (see useImageCropper.ts). Social-network style:
// a fixed circle in the middle, the photo moved and zoomed underneath it (drag / pinch on touch,
// drag / wheel with a mouse). Avatars are shown as circles everywhere, but the upload is the full
// square — the circle only shows what will be visible.

// Plenty for every avatar size the app shows (up to ~96px at 3x) without uploading originals.
const OUTPUT_SIZE = 512
const OUTPUT_QUALITY = 0.9
const ZOOM_STEP = 1.25

const { isMobile } = usePlatform()
const { request, finishCrop } = useImageCropper()

const cropperRef = ref<InstanceType<typeof Cropper> | null>(null)
const isSaving = ref(false)

const isOpen = computed({
  get: () => request.value !== null,
  set: (open: boolean) => {
    if (!open && !isSaving.value) finishCrop(null)
  },
})

// The circle fills most of the crop area whatever its size (phone sheet vs desktop dialog).
function stencilSize({ boundaries }: { boundaries: { width: number, height: number } }) {
  const size = Math.min(boundaries.width, boundaries.height) * 0.85
  return { width: size, height: size }
}

function zoom(factor: number) {
  cropperRef.value?.zoom(factor)
}

function rotate() {
  cropperRef.value?.rotate(90)
}

function toBlob(canvas: HTMLCanvasElement, type: string) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, OUTPUT_QUALITY))
}

async function save() {
  const current = request.value
  const canvas = cropperRef.value?.getResult().canvas
  if (!current || !canvas) return
  isSaving.value = true
  try {
    // WebP where the WebView can encode it (smaller, keeps transparency). WebKit (iOS/macOS) can't
    // and silently hands back PNG instead — also accepted by the backend, and also lossless for
    // transparent logos, so it's used as-is rather than falling back to JPEG.
    const blob = await toBlob(canvas, "image/webp")
    if (!blob) throw new Error("Canvas produced no image")
    const extension = blob.type === "image/webp" ? "webp" : "png"
    const baseName = current.file.name.replace(/\.[^.]+$/, "") || "avatar"
    finishCrop(new File([blob], `${baseName}.${extension}`, { type: blob.type }))
  } catch (error) {
    console.error("Failed to crop image", error)
    Sentry.captureException(error, { extra: { action: "crop_avatar" } })
    toast.error("Не удалось обрезать изображение")
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <template v-if="request">
    <Drawer v-if="isMobile" v-model:open="isOpen">
      <DrawerContent class="flex flex-col gap-0 p-0">
        <DrawerHeader class="pb-2">
          <DrawerTitle>Миниатюра</DrawerTitle>
          <DrawerDescription>Перемещайте и масштабируйте фото, чтобы выбрать видимую область.</DrawerDescription>
        </DrawerHeader>
        <!-- `data-vaul-no-drag`: dragging the photo must move the photo, not pull the sheet down. -->
        <div class="relative mx-4 overflow-hidden rounded-2xl bg-black" data-vaul-no-drag>
          <Cropper
              ref="cropperRef"
              class="h-[min(60vh,420px)] w-full"
              :src="request.src"
              :stencil-component="CircleStencil"
              :stencil-size="stencilSize"
              :stencil-props="{ handlers: {}, movable: false, resizable: false, aspectRatio: 1 }"
              image-restriction="stencil"
              :canvas="{ maxWidth: OUTPUT_SIZE, maxHeight: OUTPUT_SIZE }"
          />
        </div>
        <div class="flex items-center justify-center gap-2 py-3">
          <Button variant="secondary" size="icon-lg" class="rounded-full" aria-label="Отдалить" @click="zoom(1 / ZOOM_STEP)">
            <ZoomOut class="size-5"/>
          </Button>
          <Button variant="secondary" size="icon-lg" class="rounded-full" aria-label="Приблизить" @click="zoom(ZOOM_STEP)">
            <ZoomIn class="size-5"/>
          </Button>
          <Button variant="secondary" size="icon-lg" class="rounded-full" aria-label="Повернуть" @click="rotate">
            <RotateCw class="size-5"/>
          </Button>
        </div>
        <DrawerFooter class="pt-0 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)]">
          <Button size="xl" class="w-full rounded-xl" :disabled="isSaving" @click="save">
            <Spinner v-if="isSaving"/>
            Сохранить
          </Button>
          <Button variant="outline" size="xl" class="w-full rounded-xl" :disabled="isSaving" @click="finishCrop(null)">
            Отмена
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <Dialog v-else v-model:open="isOpen">
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Миниатюра</DialogTitle>
          <DialogDescription>Перетащите фото и прокрутите колесо мыши, чтобы выбрать видимую область.</DialogDescription>
        </DialogHeader>
        <div class="overflow-hidden rounded-xl bg-black">
          <Cropper
              ref="cropperRef"
              class="h-96 w-full"
              :src="request.src"
              :stencil-component="CircleStencil"
              :stencil-size="stencilSize"
              :stencil-props="{ handlers: {}, movable: false, resizable: false, aspectRatio: 1 }"
              image-restriction="stencil"
              :canvas="{ maxWidth: OUTPUT_SIZE, maxHeight: OUTPUT_SIZE }"
          />
        </div>
        <DialogFooter class="items-center sm:justify-between">
          <div class="flex gap-1">
            <Button variant="ghost" size="icon" aria-label="Отдалить" @click="zoom(1 / ZOOM_STEP)">
              <ZoomOut class="size-4"/>
            </Button>
            <Button variant="ghost" size="icon" aria-label="Приблизить" @click="zoom(ZOOM_STEP)">
              <ZoomIn class="size-4"/>
            </Button>
            <Button variant="ghost" size="icon" aria-label="Повернуть" @click="rotate">
              <RotateCw class="size-4"/>
            </Button>
          </div>
          <div class="flex gap-2">
            <Button variant="outline" :disabled="isSaving" @click="finishCrop(null)">Отмена</Button>
            <Button :disabled="isSaving" @click="save">
              <Spinner v-if="isSaving"/>
              Сохранить
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </template>
</template>
